import type { UploadTelemetry } from "./ports";

export function isNonBlankString(value: unknown): value is string {
	return typeof value === "string" && value.trim().length > 0;
}

export function isValidPresignedUrl(value: unknown): value is string {
	if (!isNonBlankString(value)) return false;
	try {
		const url = new URL(value);
		return url.protocol === "https:" || url.protocol === "http:";
	} catch {
		return false;
	}
}

export function isValidEtag(etag: unknown): etag is string {
	return isNonBlankString(etag);
}

export function hasValidMultipartUrls(urls: unknown, expectedCount: number): urls is string[] {
	return (
		Array.isArray(urls) &&
		Number.isInteger(expectedCount) &&
		expectedCount > 0 &&
		urls.length === expectedCount &&
		Array.from({ length: expectedCount }, (_value, index) => index).every(
			(index) => Object.prototype.hasOwnProperty.call(urls, index) && isNonBlankString(urls[index])
		)
	);
}

function hasValidPresignedUrls(urls: unknown, expectedCount: number): urls is string[] {
	return hasValidMultipartUrls(urls, expectedCount) && urls.every(isValidPresignedUrl);
}

export type MultipartTargetIdentityField = "uploadId" | "s3Key" | "bucket";

export function getInvalidMultipartTargetIdentityField(target: {
	uploadId: unknown;
	s3Key: unknown;
	bucket: unknown;
}): MultipartTargetIdentityField | undefined {
	if (!isNonBlankString(target.uploadId)) return "uploadId";
	if (!isNonBlankString(target.s3Key)) return "s3Key";
	if (!isNonBlankString(target.bucket)) return "bucket";
	return undefined;
}

export function mergeMultipartUrls(
	persistedUrls: unknown,
	fallbackUrls: readonly string[],
	updates: readonly { partNumber: number; url: string }[],
	totalChunks: number = Math.max(
		0,
		fallbackUrls.length,
		Array.isArray(persistedUrls) ? persistedUrls.length : 0,
		...updates.map((update) => update.partNumber)
	)
): string[] {
	const mergedUrls = hasValidMultipartUrls(persistedUrls, totalChunks)
		? [...persistedUrls]
		: Array.from({ length: totalChunks }, (_value, index) => fallbackUrls[index]);
	for (const update of updates) {
		const updateIndex = update.partNumber - 1;
		if (!Number.isInteger(update.partNumber) || updateIndex < 0 || updateIndex >= mergedUrls.length) continue;
		mergedUrls[updateIndex] = update.url;
	}
	return mergedUrls;
}

interface SessionUrlsShape {
	id: string;
	uploadId: string;
	chunkUrlsJson: string;
	totalChunks?: number;
}

interface SessionUrlUpdates {
	chunkUrlsJson: string;
	urlCreatedAt?: number;
}

// Concurrent chunk workers can each independently discover an expired presigned URL and call
// mergeAndPersistSessionUrls for the SAME session at roughly the same time (e.g. many parts'
// URLs crossing the TTL/safety-margin boundary together on a long-running upload). The function
// is a read-modify-write over persisted state (read current chunkUrlsJson, merge, write back);
// without serialization two concurrent calls for DIFFERENT part numbers race and the later
// write clobbers the earlier one's part update, silently losing a refreshed URL. Both current
// callers (adminSessionStore/asyncSessionStore) happen to wrap this in a Dexie "rw" transaction,
// which serializes it as a side effect of IndexedDB's native transaction locking — but that is an
// unstated caller obligation, not a guarantee this shared function makes. Serialize per session id
// here so the function is safe by construction regardless of how a future caller wires it.
const sessionUrlMergeQueues = new Map<string, Promise<unknown>>();

function enqueueSessionUrlMerge<T>(sessionId: string, task: () => Promise<T>): Promise<T> {
	const previous = sessionUrlMergeQueues.get(sessionId) ?? Promise.resolve();
	const next = previous.then(task, task);
	const placeholder = next.then(
		() => undefined,
		() => undefined
	);
	sessionUrlMergeQueues.set(sessionId, placeholder);
	void placeholder.finally(() => {
		if (sessionUrlMergeQueues.get(sessionId) === placeholder) sessionUrlMergeQueues.delete(sessionId);
	});
	return next;
}

export async function mergeAndPersistSessionUrls<Session extends SessionUrlsShape>(
	getCurrentSession: () => Promise<Session | undefined>,
	updateSession: (updates: SessionUrlUpdates) => Promise<unknown>,
	session: Session,
	updates: readonly { partNumber: number; url: string }[],
	fallbackUrls: readonly string[],
	telemetry: UploadTelemetry,
	matchesCurrentSession: (currentSession: Session, expectedSession: Session) => boolean = (currentSession, expectedSession) =>
		currentSession.uploadId === expectedSession.uploadId,
	refreshesAllPendingUrls = false
): Promise<string[]> {
	return enqueueSessionUrlMerge(session.id, () =>
		mergeAndPersistSessionUrlsUnserialized(
			getCurrentSession,
			updateSession,
			session,
			updates,
			fallbackUrls,
			telemetry,
			matchesCurrentSession,
			refreshesAllPendingUrls
		)
	);
}

async function mergeAndPersistSessionUrlsUnserialized<Session extends SessionUrlsShape>(
	getCurrentSession: () => Promise<Session | undefined>,
	updateSession: (updates: SessionUrlUpdates) => Promise<unknown>,
	session: Session,
	updates: readonly { partNumber: number; url: string }[],
	fallbackUrls: readonly string[],
	telemetry: UploadTelemetry,
	matchesCurrentSession: (currentSession: Session, expectedSession: Session) => boolean,
	refreshesAllPendingUrls: boolean
): Promise<string[]> {
	const currentSession = await getCurrentSession();
	let persistedUrls: string[] = [];
	if (currentSession) {
		try {
			const parsed = JSON.parse(currentSession.chunkUrlsJson);
			if (Array.isArray(parsed)) persistedUrls = parsed;
		} catch {
			persistedUrls = [];
		}
	}
	if (!currentSession || !matchesCurrentSession(currentSession, session)) {
		try {
			telemetry.logWarn("recording.fileUpload.urlMergeSkipped", {
				sessionId: session.id,
				uploadId: session.uploadId,
				actualUploadId: currentSession?.uploadId,
				reason: currentSession ? "session-identity-mismatch" : "session-missing"
			});
		} catch {
			// A stale merge remains a no-op even when the observability adapter is unavailable.
		}
		const returnedUrls = (persistedUrls.length > 0 ? persistedUrls : fallbackUrls).slice();
		const returnedUrlCount = returnedUrls.length;
		const returnedTotalChunks = currentSession?.totalChunks ?? session.totalChunks ?? fallbackUrls.length;
		if (!hasValidPresignedUrls(returnedUrls, returnedTotalChunks)) {
			throw new Error(`Refusing malformed multipart URL state after skipped merge: ${returnedUrlCount}/${returnedTotalChunks} URLs`);
		}
		return returnedUrls;
	}
	const totalChunks = session.totalChunks ?? fallbackUrls.length;
	const mergedUrls = mergeMultipartUrls(persistedUrls, fallbackUrls, updates, totalChunks);
	const mergedUrlCount = mergedUrls.length;
	if (!hasValidPresignedUrls(mergedUrls, totalChunks)) {
		throw new Error(
			`Persisted multipart URL merge returned ${mergedUrlCount}/${totalChunks} URLs (expected ${totalChunks} URL${
				totalChunks === 1 ? "" : "s"
			})`
		);
	}
	await updateSession({
		chunkUrlsJson: JSON.stringify(mergedUrls),
		...(refreshesAllPendingUrls ? { urlCreatedAt: Date.now() } : {})
	});
	return mergedUrls;
}

export function hasValidMultipartParts(parts: readonly { ETag: unknown; PartNumber: number }[], totalChunks: number): boolean {
	return (
		Number.isInteger(totalChunks) &&
		totalChunks > 0 &&
		parts.length === totalChunks &&
		Array.from({ length: totalChunks }, (_value, index) => index).every(
			(index) =>
				Object.prototype.hasOwnProperty.call(parts, index) &&
				parts[index].PartNumber === index + 1 &&
				isValidEtag(parts[index].ETag)
		)
	);
}
